import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminRoutes } from "../../../../routers/AdminRouters";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  LicensesType,
  PlanVariant,
  PlanBillingCycle,
} from "../../../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import {
  useGetOrganisationsQuery,
  useGetProfileQuery,
} from "../../../../services";
import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  useAddOnLicensesMutation,
  useGetPaymentDetailsQuery,
  useGetPlansQuery,
  useGetTaxesQuery,
} from "../../../../services/subscription";
import ConfirmSubscriptionModal from "./ConfirmSubscriptionModal";
import { selectOrganisationId } from "../../../../redux/organisationReducer";
import { loadRazorpayScript } from "../../../../utils/loadRazorpay";
import { subscriptionLicenseApi } from "../../../../services/subscription/subscriptionLicense.rtk.api";

interface AddonScreensModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  license?: any;
  onPaymentFailed?: () => void;
}

const AddonScreensModal: React.FC<AddonScreensModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  license,
  onPaymentFailed
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [billingCycle, setBillingCycle] = useState<PlanBillingCycle>(
    PlanBillingCycle.MONTHLY
  );
  const [licenseType, setLicenseType] = useState<LicensesType>(
    (license?.type as LicensesType) || LicensesType.BASIC
  );
  const [licenseCount, setLicenseCount] = useState(1);
  const { admin, isLoggedIn, profile } = useAppSelector(
    (state: RootState) => state.admin
  );
  const organisationId = useAppSelector(selectOrganisationId);

  const { data: profileResp } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: organisationsResp, isLoading: orgsLoading } =
    useGetOrganisationsQuery(undefined, {
      skip: !isLoggedIn,
    });

  // Fetch plans
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery(
    undefined,
    {
      skip: !isLoggedIn,
    }
  );
  const { data: taxesResp, isLoading: taxesLoading } = useGetTaxesQuery(
    undefined,
    {
      skip: !isLoggedIn,
    }
  );

  const [addLicenses, { isLoading: adding }] = useAddOnLicensesMutation();

  const [showConfirm, setShowConfirm] = useState(false);

  const assignedOrgsRaw = (profileResp as any)?.result?.assignedOrganisations;
  const assignedOrgIds: string[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw
      .map((o: any) => (typeof o === "string" ? o : o?._id))
      .filter(Boolean)
    : [];
  const assignedOrgsObjs: any[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw.filter((o: any) => typeof o === "object" && o)
    : [];

  const isSuperAdmin = String(profile?.role || "").toLowerCase() === "superadmin";

  const allOrgs: any[] = Array.isArray((organisationsResp as any)?.data)
    ? (organisationsResp as any).data
    : Array.isArray(organisationsResp)
      ? (organisationsResp as any[])
      : [];

  const filteredOrgs = isSuperAdmin
    ? allOrgs
    : allOrgs.filter((org) => assignedOrgIds.includes(org?._id));
  const options =
    filteredOrgs.length > 0
      ? filteredOrgs.map((org) => ({
        value: String(org?._id),
        label: String((org?.displayName || org?.name) ?? org?._id),
        currency: org?.currency ?? "",
      }))
      : assignedOrgsObjs.length > 0
        ? assignedOrgsObjs.map((org) => ({
          value: String(org?._id),
          label: String((org?.displayName || org?.name) ?? org?._id),
        }))
        : assignedOrgIds.map((id) => ({ value: String(id), label: String(id) }));

  const getDefaultSelectedOrgId = () => {
    if (license?.organisation?._id) return String(license.organisation._id);
    if (typeof license?.organisation === "string") return String(license.organisation);
    if (organisationId) return organisationId;
    if (assignedOrgIds.length > 0) return assignedOrgIds[0];
    return "";
  };

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const { data: paymentDta } = useGetPaymentDetailsQuery({ organisationId: selectedOrgId || organisationId });

  useEffect(() => {
    if (license?.organisation?._id) {
      setSelectedOrgId(license.organisation._id);
    } else if (typeof license?.organisation === "string") {
      setSelectedOrgId(license.organisation);
    } else if (organisationId) {
      setSelectedOrgId(organisationId);
    } else if (!selectedOrgId && assignedOrgIds.length > 0) {
      setSelectedOrgId(assignedOrgIds[0]);
    }
  }, [organisationId, assignedOrgIds, license]);

  const resetModalState = () => {
    setBillingCycle((license?.cycle as PlanBillingCycle) || PlanBillingCycle.MONTHLY);
    setLicenseType((license?.type as LicensesType) || LicensesType.BASIC);
    setLicenseCount(1);
    setSelectedOrgId(getDefaultSelectedOrgId());
    setShowConfirm(false);
  };

  const handleModalClose = () => {
    resetModalState();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setBillingCycle((license?.cycle as PlanBillingCycle) || PlanBillingCycle.MONTHLY);
      setLicenseType((license?.type as LicensesType) || LicensesType.BASIC);
      setLicenseCount(1);
      setSelectedOrgId(getDefaultSelectedOrgId());
    }
  }, [isOpen, license, organisationId, assignedOrgIds]);

  const incrementCount = () => setLicenseCount((prev) => prev + 1);
  const decrementCount = () =>
    setLicenseCount((prev) => (prev > 1 ? prev - 1 : 1));

  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
      ? (plansResp as any).data.results
      : Array.isArray(plansResp)
        ? (plansResp as any[])
        : [];

  // Filter only addon plans for the selected license's plan
  const licensePlanId =
    typeof license?.plan === "object" ? license?.plan?._id : license?.plan;

  const addonPlans = Array.isArray(plans)
    ? plans.filter((p: any) => p?.variant === PlanVariant.ADDON_SCREEN)
    : [];

  const matchesBasePlan = (p: any) =>
    p?.basePlan === licensePlanId || p?.basePlan?._id === licensePlanId;

  const activePlan =
    addonPlans.find(
      (p: any) =>
        matchesBasePlan(p) && (!license?.cycle || p?.cycle === license?.cycle)
    ) || addonPlans.find((p: any) => matchesBasePlan(p));

  // Set billing cycle to match license if available, mostly visual
  useEffect(() => {
    if (license?.cycle) {
      setBillingCycle(license.cycle as PlanBillingCycle);
    }
    if (license?.type) {
      setLicenseType(license.type as LicensesType);
    }
  }, [license]);

  // Remove unused effects that switched between basic/premium

  // Find selected org object to get currency and country
  const selectedOrgObj =
    filteredOrgs.find((org) => String(org?._id) === selectedOrgId) ||
    assignedOrgsObjs.find((org) => String(org?._id) === selectedOrgId);

  const paymentProvider = selectedOrgObj?.gateway?.provider?.toLowerCase() || "stripe";
  const isRazorpay = paymentProvider === "razorpay";
  const needsCardFirst = !isRazorpay && paymentDta?.results?.length === 0;


  console.log(paymentProvider, "payment proiveder")
  useEffect(() => {
    if (isRazorpay) {
      setLicenseCount(1);
    }
  }, [isRazorpay]);

  const selectedOrgCountry = selectedOrgObj?.country?.toLowerCase().trim();
  const taxesList = (taxesResp as any)?.results || [];

  const countryMap: Record<string, string> = {
    "united arab emirates": "ae",
    "india": "in",
    "ae": "ae",
    "in": "in",
  };

  const appliedTax = taxesList.find((t: any) => {
    const taxCountry = t.country?.toLowerCase().trim();
    return (
      taxCountry === selectedOrgCountry ||
      taxCountry === countryMap[selectedOrgCountry || ""] ||
      countryMap[taxCountry || ""] === selectedOrgCountry
    );
  });

  const selectedCurrency = selectedOrgObj?.currency?.toUpperCase?.() || "INR";

  const currencySymbols: Record<string, string> = {
    INR: "₹",
    AED: "د.إ",
    USD: "$",
  };
  const currencySymbol = currencySymbols[selectedCurrency] || selectedCurrency;

  const pickPrice = (plan: any, currency = "INR") => {
    const prices = Array.isArray(plan?.prices) ? plan.prices : [];
    return (
      prices.find((x: any) => x?.currency?.toUpperCase?.() === currency) ||
      prices[0]
    );
  };

  const breakdown = (count: number, plan: any) => {
    if (!plan || !count) {
      return {
        subtotal: 0,
        tax: 0,
        total: 0,
        unitPrice: 0,
        taxPercent: 0,
        taxName: "",
      };
    }

    const priceObj = pickPrice(plan, selectedCurrency);
    const unitPrice = Number(priceObj?.price ?? 0);
    const subtotal = count * unitPrice;

    const isInclusive = appliedTax
      ? !!appliedTax.inclusive
      : !!priceObj?.tax?.inclusive;

    const taxPercent = appliedTax
      ? Number(appliedTax.rate ?? 0)
      : Number(priceObj?.tax?.percent ?? 0);

    const taxName = appliedTax ? appliedTax.name : (priceObj?.tax?.name || "");

    let tax = 0;
    let total = subtotal;

    if (taxPercent > 0) {
      if (!isInclusive) {
        tax = subtotal * (taxPercent / 100);
        total = subtotal + tax;
      } else {
        tax = subtotal - subtotal / (1 + taxPercent / 100);
        total = subtotal;
      }
    }

    return { subtotal, tax, total, unitPrice, taxPercent, taxName, isInclusive };
  };

  const activeBD = breakdown(licenseCount, activePlan);

  const subTotal = activeBD.subtotal;
  const taxes = activeBD.tax;
  const grandTotal = activeBD.total;

  const handleSubscribe = async () => {
    const products: { plan: string; quantity: number }[] = [];

    if (licenseCount > 0 && activePlan?._id) {
      products.push({ plan: activePlan._id, quantity: licenseCount });
    }

    if (!selectedOrgId || products.length === 0) return;

    try {
      const resp = await addLicenses({
        licenseId: license?._id,
        organisationId: selectedOrgId,
        products,
        planId: activePlan?._id,
        quantity: licenseCount,
        currentBilledAmount: Number(grandTotal.toFixed(2)),
        currentBilledTaxAmount: Number(taxes.toFixed(2)),
      }).unwrap();

      if (isRazorpay) {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          console.error("Razorpay SDK failed to load.");
          return;
        }

        const subId =
          resp?.result?.subscription_id ||
          resp?.result?.subscriptionId ||
          resp?.result?.id ||
          resp?.result?.invoice?.id;

        const options = {
          key: import.meta.env.VITE_RAZORPAY_KEY_ID || "rzp_test_SYG64vDtBFhNYR",
          subscription_id: subId,
          name: selectedOrgObj?.displayName || selectedOrgObj?.name || "Subscription",
          description: "Activate Addon Screens",
          handler: function (response: any) {
            console.log("Razorpay payment successful", response);
            dispatch(subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses", "SubscriptionScreens", "LicensesAddon"]));
            onSuccess?.();
            handleModalClose();
          },
          theme: {
            color: "#C6D92C",
          },
          modal: {
            ondismiss: function () {
              console.log("Razorpay closed");

              onPaymentFailed?.(); // ✅ THIS WAS MISSING
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        onSuccess?.();
        if (resp?.result?.invoice?.invoiceUrl) {
          window.open(resp.result.invoice.invoiceUrl, "_blank");
          handleModalClose();
        } else {
          handleModalClose();
        }
      }
    } catch {
      // optionally handle error
    }
  };

  if (!isOpen) return null;

  const isSubscribeDisabled =
    adding || !selectedOrgId || licenseCount === 0 || !activePlan;

  return (
    <div
      className="fixed inset-0 z-50 flex items-right justify-end bg-black/40"
      onClick={handleModalClose}
    >
      <div
        className="w-80 h-full rounded-[4px] bg-black shadow-[0_0_25px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-80 h-full ${isDark ? "bg-[#1B1B1B]" : "bg-white"}`}>
          {/* Header */}
          <div
            className={`flex items-center justify-between px-4 py-2 mb-2 mt-6 border-b ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
              }`}
          >
            <h3
              className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"
                }`}
            >
              Add New Addon Screens
            </h3>
            <button
              onClick={handleModalClose}
              className={`w-6 h-6 flex items-center justify-center rounded-[7.529px] transition-opacity ${isDark
                ? "bg-[#2E2E2E] shadow-[0_-0.941px_0.941px_0_rgba(150,161,172,0.12)_inset] hover:opacity-80"
                : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.4235 2.63525L2.63525 12.4235M2.63525 2.63525L12.4235 12.4235"
                  stroke={isDark ? "#F3F3F6" : "#4B5563"}
                  strokeWidth="1.12942"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3 space-y-4 text-[11px] leading-relaxed">
            {/* Organization (read-only) */}
            <div className="space-y-1">
              <div
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Organization
              </div>
              <div
                className={`w-full h-7 px-2 flex items-center rounded-sm border text-[11px] ${isDark
                  ? "border-[#3A3A3A] bg-[#101010] text-gray-100"
                  : "border-gray-300 bg-white text-gray-900"
                  }`}
              >
                {license?.organisation?.displayName || license?.organisation?.name}
              </div>
            </div>

            {/* License Type & Cost by row */}
            <div className="flex w-full gap-3">
              <div className="flex-1 space-y-1">
                <div
                  className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                >
                  License Type
                </div>
                <div
                  className={`w-full h-7 px-2 flex items-center rounded-sm border text-[11px] ${isDark
                    ? "border-none text-gray-100"
                    : "border-none bg-white text-gray-900"
                    }`}
                >
                  {String(licenseType || license?.type || LicensesType.BASIC)
                    .toUpperCase()
                    .includes("PREMIUM")
                    ? "Premium"
                    : "Basic"}
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <div
                  className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                >
                  Cost by
                </div>
                <div
                  className={`w-full h-7 px-2 flex items-center rounded-sm border text-[11px] ${isDark
                    ? "border-none  text-gray-100"
                    : "border-none bg-white text-gray-900"
                    }`}
                >
                  {billingCycle === PlanBillingCycle.ANNUALLY
                    ? "Annually"
                    : "Monthly"}
                </div>
              </div>
            </div>

            {/* Add Addon Screens */}
            <div className={`space-y-1 ${isRazorpay ? "opacity-50 pointer-events-none" : ""}`}>
              <div
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Add Addon Screens
              </div>
              <div
                className={`flex items-center justify-between h-8 rounded-full border px-2 ${isDark
                  ? "border-[#3A3A3A] bg-[#101010]"
                  : "border-gray-300 bg-gray-100"
                  }`}
              >
                <button
                  type="button"
                  onClick={decrementCount}
                  className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs leading-none ${isDark
                    ? "border-[#3A3A3A] text-gray-300 hover:bg-white/10"
                    : "border-gray-400 text-gray-700 hover:bg-black/5"
                    }`}

                >
                  <span>-</span>
                </button>
                <span
                  className={`text-xs font-semibold tracking-widest ${isDark ? "text-gray-100" : "text-gray-900"
                    }`}
                >
                  {licenseCount.toString().padStart(2, "0")}
                </span>
                <button
                  type="button"
                  onClick={incrementCount}
                  className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs leading-none ${isDark
                    ? "border-[#3A3A3A] text-gray-300 hover:bg-white/10"
                    : "border-gray-400 text-gray-700 hover:bg-black/5"
                    }`}
                >
                  <span>+</span>
                </button>
              </div>
            </div>

            {/* Cost Details */}
            <div className="pt-1">
              <div
                className={`text-[12px] font-medium mb-2 border-b ${isDark
                  ? "text-gray-300 border-[#3A3A3A]"
                  : "text-gray-800 border-gray-200"
                  }`}
              >
                Cost Details
              </div>
              <div className="space-y-1 text-[10px]">
                <div
                  className={`flex justify-between pb-1 ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <span>No. Screens</span>
                  <span>Cost {currencySymbol}</span>
                </div>
                <div
                  className={`flex justify-between mt-1 pt-1 border-b ${isDark
                    ? "text-gray-100 border-[#3A3A3A]"
                    : "text-gray-900 border-gray-200"
                    }`}
                >
                  <span>{licenseCount}</span>
                  <span>{subTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between pt-2 border-b ${isDark
                    ? "text-gray-400 border-[#3A3A3A]"
                    : "text-gray-500 border-gray-200"
                    }`}
                >
                  <span>Due Total</span>
                  <span>{subTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <span>
                    Tax -{activeBD.taxName}{" "}
                    {activeBD.taxPercent > 0
                      ? `(${activeBD.taxPercent}% ${activeBD.isInclusive ? "inc." : "exc."
                      })`
                      : ""}
                  </span>
                  <span>{taxes.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between font-semibold pb-2 ${isDark ? "text-gray-100" : "text-gray-900"
                    }`}
                >
                  <span>Total</span>
                  <span>{grandTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`pt-2 text-[10px] leading-snug flex items-start gap-2 ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="flex-shrink-0 mt-[1px]"
                  >
                    <path
                      d="M7.00033 12.8334C10.222 12.8334 12.8337 10.2217 12.8337 7.00008C12.8337 3.77842 10.222 1.16675 7.00033 1.16675C3.77866 1.16675 1.16699 3.77842 1.16699 7.00008C1.16699 10.2217 3.77866 12.8334 7.00033 12.8334Z"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 9.33325V6.70825"
                      stroke="#C6D92C"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 4.67356V4.66772"
                      stroke="#C6D92C"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p>
                    Future invoices will be billed on a monthly basis. The
                    current amount reflects charges for the remaining days.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className={`px-4 pb-3 pt-3 space-y-2 mt-4  `}>
            <button
              className={`w-full h-10 text-[11px] mb-3 mt-4 font-semibold text-black rounded-[10px] border mb-1 ${isDark ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                border: "1px solid var(--menu-active-border, #C6D92C)",
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
              onClick={() => {
                if (isRazorpay) {
                  handleSubscribe();
                } else if (needsCardFirst) {
                  navigate(adminRoutes.paymentList);
                } else {
                  setShowConfirm(true);
                }
              }}
              disabled={
                (!needsCardFirst) && isSubscribeDisabled
              }
            >
              {needsCardFirst
                ? "Add Your First Card"
                : adding
                  ? "Adding..."
                  : "Add"}
            </button>
            <button
              onClick={handleModalClose}
              className={`w-full h-10 text-[11px] font-medium rounded-[10px] border ${isDark
                ? "border-[#3A3A3A] text-gray-300 hover:bg-white/5"
                : "border-gray-300 text-gray-700 hover:bg-black/5"
                }`}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmSubscriptionModal
        isOpen={showConfirm}
        onConfirm={async () => {
          await handleSubscribe();
          setShowConfirm(false);
        }}
        onClose={() => setShowConfirm(false)}
        disabled={isSubscribeDisabled || adding}
        message={`This will add ${licenseCount} addon screen(s) to the ${String(licenseType || license?.type || LicensesType.BASIC).toUpperCase().includes("PREMIUM") ? "Premium" : "Basic"
          } license for ${license?.organisation?.displayName || license?.organisation?.name || selectedOrgObj?.displayName || selectedOrgObj?.name || "selected organization"
          }. You can cancel anytime.`}
      />
    </div>
  );
};

export default AddonScreensModal;
